import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Sparkles, 
  Clock, 
  Zap, 
  Thermometer, 
  AlertTriangle,
  ArrowRight,
  FileText,
  Upload,
  Download,
  ClipboardList,
  FileUp,
  Info,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Activity,
  History
} from "lucide-react";
import { SCENARIO_TEMPLATES, getRiskLevelBadge } from "../data/scenarioTemplates";
import { assessAsset, generateCSVTemplate, parseCSVToAsset } from "../utils/assetAssessment";
import { toast } from "sonner";

const iconMap = {
  sparkles: Sparkles,
  clock: Clock,
  zap: Zap,
  thermometer: Thermometer,
  "alert-triangle": AlertTriangle
};

// Quick Entry Form Component with Progressive Disclosure
const QuickEntryForm = ({ onAssess }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState({
    // Basic fields
    assetName: "",
    ratedPower: "",
    voltageHV: "",
    voltageLV: "",
    age: "",
    coolingType: "",
    avgLoad: "",
    peakLoad: "",
    peakTemp: "",
    oilCondition: "",
    // Advanced DGA fields
    dgaH2: "",
    dgaCH4: "",
    dgaC2H2: "",
    dgaC2H4: "",
    dgaC2H6: "",
    dgaCO: "",
    dgaCO2: "",
    // Advanced operational fields
    loadPattern: "",
    ambientTemp: "",
    // Diagnostic history
    lastTestDate: "",
    diagnosticNotes: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.assetName || !formData.ratedPower || !formData.age || !formData.avgLoad) {
      toast.error("Please fill in required fields: Asset Name, Power Rating, Age, and Average Load");
      return;
    }
    
    // Build DGA object if any DGA values provided
    const dgaData = {};
    if (formData.dgaH2) dgaData.h2 = parseFloat(formData.dgaH2);
    if (formData.dgaCH4) dgaData.ch4 = parseFloat(formData.dgaCH4);
    if (formData.dgaC2H2) dgaData.c2h2 = parseFloat(formData.dgaC2H2);
    if (formData.dgaC2H4) dgaData.c2h4 = parseFloat(formData.dgaC2H4);
    if (formData.dgaC2H6) dgaData.c2h6 = parseFloat(formData.dgaC2H6);
    if (formData.dgaCO) dgaData.co = parseFloat(formData.dgaCO);
    if (formData.dgaCO2) dgaData.co2 = parseFloat(formData.dgaCO2);
    
    // Convert string values to numbers where needed
    const assetData = {
      assetName: formData.assetName,
      ratedPower: parseFloat(formData.ratedPower) || 0,
      voltageHV: parseFloat(formData.voltageHV) || null,
      voltageLV: parseFloat(formData.voltageLV) || null,
      age: parseFloat(formData.age) || 0,
      coolingType: formData.coolingType || "N/A",
      avgLoad: parseFloat(formData.avgLoad) || 0,
      peakLoad: parseFloat(formData.peakLoad) || parseFloat(formData.avgLoad) || 0,
      peakTemp: formData.peakTemp ? parseFloat(formData.peakTemp) : null,
      oilCondition: formData.oilCondition || null,
      ambientTemp: formData.ambientTemp ? parseFloat(formData.ambientTemp) : null,
      dga: Object.keys(dgaData).length > 0 ? dgaData : null,
      loadPattern: formData.loadPattern || null,
      lastTestDate: formData.lastTestDate || null,
      diagnosticNotes: formData.diagnosticNotes || null
    };
    
    onAssess(assetData);
  };

  // Check if any advanced data is filled
  const hasAdvancedData = formData.dgaH2 || formData.dgaCH4 || formData.dgaC2H2 || 
    formData.dgaC2H4 || formData.dgaC2H6 || formData.dgaCO || formData.dgaCO2 ||
    formData.loadPattern || formData.ambientTemp || formData.lastTestDate || formData.diagnosticNotes;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Basic Information Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <ClipboardList className="w-4 h-4 text-blue-600" />
          Basic Information
          <span className="text-xs text-slate-400 font-normal">* Required</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Asset Name */}
          <div className="col-span-2">
            <Label htmlFor="assetName" className="text-xs">Asset Name / ID *</Label>
            <Input
              id="assetName"
              placeholder="e.g., Substation A - TX-001"
              value={formData.assetName}
              onChange={(e) => handleChange("assetName", e.target.value)}
              className="h-8 text-sm"
              data-testid="asset-name-input"
            />
          </div>
          
          {/* Power Rating & Age */}
          <div>
            <Label htmlFor="ratedPower" className="text-xs">Rated Power (MVA) *</Label>
            <Input
              id="ratedPower"
              type="number"
              placeholder="e.g., 50"
              value={formData.ratedPower}
              onChange={(e) => handleChange("ratedPower", e.target.value)}
              className="h-8 text-sm"
              data-testid="rated-power-input"
            />
          </div>
          
          <div>
            <Label htmlFor="age" className="text-xs">Age (Years) *</Label>
            <Input
              id="age"
              type="number"
              placeholder="e.g., 15"
              value={formData.age}
              onChange={(e) => handleChange("age", e.target.value)}
              className="h-8 text-sm"
              data-testid="age-input"
            />
          </div>
          
          {/* Voltages */}
          <div>
            <Label htmlFor="voltageHV" className="text-xs">Voltage HV (kV)</Label>
            <Input
              id="voltageHV"
              type="number"
              placeholder="e.g., 132"
              value={formData.voltageHV}
              onChange={(e) => handleChange("voltageHV", e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          
          <div>
            <Label htmlFor="voltageLV" className="text-xs">Voltage LV (kV)</Label>
            <Input
              id="voltageLV"
              type="number"
              placeholder="e.g., 33"
              value={formData.voltageLV}
              onChange={(e) => handleChange("voltageLV", e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          
          {/* Cooling & Oil */}
          <div>
            <Label htmlFor="coolingType" className="text-xs">Cooling Type</Label>
            <select
              id="coolingType"
              value={formData.coolingType}
              onChange={(e) => handleChange("coolingType", e.target.value)}
              className="w-full h-8 text-sm border rounded-md px-2 bg-white"
              data-testid="cooling-type-select"
            >
              <option value="">Select...</option>
              <option value="ONAN">ONAN</option>
              <option value="ONAF">ONAF</option>
              <option value="ONAN/ONAF">ONAN/ONAF</option>
              <option value="OFAF">OFAF</option>
              <option value="ODAF">ODAF</option>
            </select>
          </div>
          
          <div>
            <Label htmlFor="oilCondition" className="text-xs">Oil Condition</Label>
            <select
              id="oilCondition"
              value={formData.oilCondition}
              onChange={(e) => handleChange("oilCondition", e.target.value)}
              className="w-full h-8 text-sm border rounded-md px-2 bg-white"
              data-testid="oil-condition-select"
            >
              <option value="">Select...</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </select>
          </div>
          
          {/* Load */}
          <div>
            <Label htmlFor="avgLoad" className="text-xs">Average Load (%) *</Label>
            <Input
              id="avgLoad"
              type="number"
              placeholder="e.g., 55"
              value={formData.avgLoad}
              onChange={(e) => handleChange("avgLoad", e.target.value)}
              className="h-8 text-sm"
              data-testid="avg-load-input"
            />
          </div>
          
          <div>
            <Label htmlFor="peakLoad" className="text-xs">Peak Load (%)</Label>
            <Input
              id="peakLoad"
              type="number"
              placeholder="e.g., 75"
              value={formData.peakLoad}
              onChange={(e) => handleChange("peakLoad", e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          
          {/* Peak Temperature */}
          <div className="col-span-2">
            <Label htmlFor="peakTemp" className="text-xs">Peak Winding Temperature (°C)</Label>
            <Input
              id="peakTemp"
              type="number"
              placeholder="e.g., 85"
              value={formData.peakTemp}
              onChange={(e) => handleChange("peakTemp", e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Advanced Details Toggle */}
      <div className="border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
            showAdvanced ? "bg-amber-50 border border-amber-200" : "bg-slate-50 hover:bg-slate-100 border border-slate-200"
          }`}
          data-testid="advanced-toggle-btn"
        >
          <div className="flex items-center gap-2">
            <FlaskConical className={`w-4 h-4 ${showAdvanced ? "text-amber-600" : "text-slate-500"}`} />
            <span className={`text-sm font-medium ${showAdvanced ? "text-amber-700" : "text-slate-600"}`}>
              Advanced Details
            </span>
            {hasAdvancedData && !showAdvanced && (
              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">Data entered</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Optional - enhances assessment accuracy</span>
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>
      </div>

      {/* Advanced Details Section (Collapsible) */}
      {showAdvanced && (
        <div className="space-y-4 p-3 bg-slate-50 rounded-lg border border-slate-200 animate-in slide-in-from-top-2 duration-200">
          
          {/* DGA Report Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-slate-700">Latest DGA Report (ppm)</span>
              <span className="text-xs text-slate-400">— Critical for fault detection</span>
            </div>
            
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label htmlFor="dgaH2" className="text-xs text-slate-500">H₂</Label>
                <Input
                  id="dgaH2"
                  type="number"
                  placeholder="Hydrogen"
                  value={formData.dgaH2}
                  onChange={(e) => handleChange("dgaH2", e.target.value)}
                  className="h-7 text-xs"
                  data-testid="dga-h2-input"
                />
              </div>
              <div>
                <Label htmlFor="dgaCH4" className="text-xs text-slate-500">CH₄</Label>
                <Input
                  id="dgaCH4"
                  type="number"
                  placeholder="Methane"
                  value={formData.dgaCH4}
                  onChange={(e) => handleChange("dgaCH4", e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="dgaC2H2" className="text-xs text-slate-500">C₂H₂</Label>
                <Input
                  id="dgaC2H2"
                  type="number"
                  placeholder="Acetylene"
                  value={formData.dgaC2H2}
                  onChange={(e) => handleChange("dgaC2H2", e.target.value)}
                  className="h-7 text-xs"
                  data-testid="dga-c2h2-input"
                />
              </div>
              <div>
                <Label htmlFor="dgaC2H4" className="text-xs text-slate-500">C₂H₄</Label>
                <Input
                  id="dgaC2H4"
                  type="number"
                  placeholder="Ethylene"
                  value={formData.dgaC2H4}
                  onChange={(e) => handleChange("dgaC2H4", e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="dgaC2H6" className="text-xs text-slate-500">C₂H₆</Label>
                <Input
                  id="dgaC2H6"
                  type="number"
                  placeholder="Ethane"
                  value={formData.dgaC2H6}
                  onChange={(e) => handleChange("dgaC2H6", e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="dgaCO" className="text-xs text-slate-500">CO</Label>
                <Input
                  id="dgaCO"
                  type="number"
                  placeholder="Carbon Mon."
                  value={formData.dgaCO}
                  onChange={(e) => handleChange("dgaCO", e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="dgaCO2" className="text-xs text-slate-500">CO₂</Label>
                <Input
                  id="dgaCO2"
                  type="number"
                  placeholder="Carbon Diox."
                  value={formData.dgaCO2}
                  onChange={(e) => handleChange("dgaCO2", e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
            </div>
            
            <p className="text-xs text-slate-400 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Enter values from your latest DGA report. Acetylene (C₂H₂) is critical for arcing detection.
            </p>
          </div>

          {/* Load Curve Section */}
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-700">Load & Environment</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="loadPattern" className="text-xs text-slate-500">Typical Load Pattern</Label>
                <select
                  id="loadPattern"
                  value={formData.loadPattern}
                  onChange={(e) => handleChange("loadPattern", e.target.value)}
                  className="w-full h-8 text-sm border rounded-md px-2 bg-white"
                  data-testid="load-pattern-select"
                >
                  <option value="">Select pattern...</option>
                  <option value="constant">Constant (Base Load)</option>
                  <option value="daily_cycling">Daily Cycling (Day/Night)</option>
                  <option value="seasonal">Seasonal Variation</option>
                  <option value="peaky">Peaky / Intermittent</option>
                  <option value="growing">Steadily Growing</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="ambientTemp" className="text-xs text-slate-500">Avg. Ambient Temp (°C)</Label>
                <Input
                  id="ambientTemp"
                  type="number"
                  placeholder="e.g., 35"
                  value={formData.ambientTemp}
                  onChange={(e) => handleChange("ambientTemp", e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Diagnostic History Section */}
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-slate-700">Diagnostic History</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lastTestDate" className="text-xs text-slate-500">Last Major Test Date</Label>
                <Input
                  id="lastTestDate"
                  type="date"
                  value={formData.lastTestDate}
                  onChange={(e) => handleChange("lastTestDate", e.target.value)}
                  className="h-8 text-sm"
                  data-testid="last-test-date-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="diagnosticNotes" className="text-xs text-slate-500">Previous Findings / Notes</Label>
                <Textarea
                  id="diagnosticNotes"
                  placeholder="e.g., Minor oil leak observed in 2022, repaired. SFRA baseline taken in 2020."
                  value={formData.diagnosticNotes}
                  onChange={(e) => handleChange("diagnosticNotes", e.target.value)}
                  className="text-sm h-16 resize-none"
                  data-testid="diagnostic-notes-textarea"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Button type="submit" className="w-full" data-testid="generate-assessment-btn">
        <ClipboardList className="w-4 h-4 mr-2" />
        Generate Assessment
      </Button>
    </form>
  );
};

const ScenarioSelectionModal = ({ isOpen, onClose, onSelectScenario }) => {
  const [activeTab, setActiveTab] = useState("demo");
  const [uploadMode, setUploadMode] = useState("form"); // 'form' or 'file'
  const fileInputRef = useRef(null);

  const handleSelect = (scenario) => {
    onSelectScenario(scenario);
    onClose();
  };

  const handleAssessAsset = (assetData) => {
    try {
      const assessment = assessAsset(assetData);
      onSelectScenario(assessment);
      onClose();
      toast.success("Assessment generated successfully!");
    } catch (error) {
      toast.error("Failed to generate assessment: " + error.message);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result;
        const assetData = parseCSVToAsset(csvText);
        
        if (!assetData.assetName || !assetData.ratedPower || !assetData.age) {
          toast.error("CSV missing required fields: asset_name, rated_power_mva, age_years");
          return;
        }
        
        handleAssessAsset(assetData);
      } catch (error) {
        toast.error("Failed to parse CSV file");
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const template = generateCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset_assessment_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="scenario-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-blue-600" />
            Diagnostic Scenarios
          </DialogTitle>
        </DialogHeader>

        {/* Tab Buttons */}
        <div className="flex gap-2 border-b border-slate-200 pb-2 flex-shrink-0">
          <button
            onClick={() => setActiveTab("demo")}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "demo"
                ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <ClipboardList className="w-4 h-4 inline mr-2" />
            Demo Scenarios
          </button>
          <button
            onClick={() => setActiveTab("custom")}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === "custom"
                ? "bg-green-50 text-green-700 border-b-2 border-green-600"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Upload Asset Data
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {activeTab === "demo" ? (
            <div className="space-y-3">
              {SCENARIO_TEMPLATES.map((scenario) => {
                const IconComponent = iconMap[scenario.icon] || FileText;
                const riskBadge = getRiskLevelBadge(scenario.risk_level);
                
                return (
                  <Card 
                    key={scenario.id}
                    className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-300 group"
                    onClick={() => handleSelect(scenario)}
                    data-testid={`scenario-${scenario.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${scenario.risk_color}15` }}
                        >
                          <IconComponent 
                            className="w-6 h-6" 
                            style={{ color: scenario.risk_color }}
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                              {scenario.name}
                            </h3>
                            <Badge className={`text-xs ${riskBadge.color} border`}>
                              {riskBadge.text}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-2">
                            {scenario.shortDescription}
                          </p>
                          
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              {scenario.prescribed_tests.priority_1.length} Priority Tests
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>
                              {scenario.prescribed_monitoring.critical.length + scenario.prescribed_monitoring.recommended.length} Monitoring
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0 self-center">
                          <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Custom Asset Assessment</p>
                  <p className="text-green-700">
                    Enter your transformer details to get a personalized diagnostic prescription. 
                    For bulk uploads or integration with DMS Insight, use the CSV template.
                  </p>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2">
                <Button
                  variant={uploadMode === "form" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setUploadMode("form")}
                >
                  Quick Entry
                </Button>
                <Button
                  variant={uploadMode === "file" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setUploadMode("file")}
                >
                  <FileUp className="w-4 h-4 mr-1" />
                  Upload CSV
                </Button>
              </div>

              {uploadMode === "form" ? (
                <QuickEntryForm onAssess={handleAssessAsset} />
              ) : (
                <div className="space-y-4">
                  {/* Download Template */}
                  <Card className="border-dashed border-2 border-slate-300">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">Step 1: Download Template</h4>
                          <p className="text-sm text-slate-500">
                            Get the CSV template with all fields for asset data
                          </p>
                        </div>
                        <Button variant="outline" onClick={handleDownloadTemplate}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Upload File */}
                  <Card className="border-dashed border-2 border-green-300 bg-green-50/30">
                    <CardContent className="p-4">
                      <div className="text-center">
                        <h4 className="font-medium text-slate-900 mb-2">Step 2: Upload Filled Template</h4>
                        <p className="text-sm text-slate-500 mb-4">
                          Fill in your asset data and upload the CSV file
                        </p>
                        <input
                          type="file"
                          accept=".csv"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <Button onClick={() => fileInputRef.current?.click()}>
                          <Upload className="w-4 h-4 mr-2" />
                          Select CSV File
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* DMS Insight Integration Note */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-800">
                        <p className="font-medium">DMS Insight Integration</p>
                        <p className="text-blue-700">
                          Coming soon: Export asset data directly from DMS Insight in the compatible format 
                          for seamless assessment integration.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-400 text-center">
            {activeTab === "demo" 
              ? "Demo scenarios provide prescriptions based on common real-world situations"
              : "Custom assessments generate prescriptions based on your specific asset data"
            }
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScenarioSelectionModal;
